import React, { useContext, useState } from 'react';
import { TransactionResult, useSearchService } from '../../../sdk/SearchServiceClient';
import { Error } from '@app/shared';
import { AccountContext } from '../AccountContext';
import { toCurrency, toFormattedDate } from '@app/utilities';
import { useAsyncEffect } from '@app/hooks';
import Adjustment from './Adjustment';
import Modal from 'react-overlays/Modal';
import styled from 'styled-components';
import './TransactionSearch.css';
import { useGlobalContext } from '../../../GlobalContextProvider';
import { CopyToClipboardIcon } from '../../../shared/CopyToClipboardIcon';

const Backdrop = styled('div')`
  position: fixed;
  z-index: 1040;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #000;
  opacity: 0.25;
`;

const AdjustmentModal = styled(Modal)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: fit-content;
  z-index: 1040;
  background-color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  padding: 20px;
`;

export function TransactionSearch() {
  const [show, setShow] = useState(false);
  const [transaction, setTransaction] = useState<TransactionResult>(null);
  const renderBackdrop = (props) => <Backdrop {...props} />;
  const { accountId } = useContext(AccountContext);
  const { tenantId } = useGlobalContext();
  const searchService = useSearchService(tenantId);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [searchResults, setSearchResults] = useState<TransactionResult[]>([]);
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [transactionUpdateVersion, setTransactionUpdateVersion] = useState<number>(1);
  const transactionStateUpdatedCallback = () => setTransactionUpdateVersion(transactionUpdateVersion + 1);

  useAsyncEffect(async () => {
    try {
      setIsLoading(true);
      const searchResponse = await searchService.getAccountTransactions(accountId);
      setIsLoading(false);
      setSearchResults(searchResponse.value);
    } catch (error) {
      setIsLoading(false);
      setError(error);
    }
  }, [accountId, transactionUpdateVersion]);

  return (
    <div className="ui segment">
      <Error error={error} />
      {isLoading && <div className="ui active centered inline loader" />}

      {!isLoading && searchResults.length > 0 && (
        <div className="scrollable">
          <table className="ui single line table">
            <thead>
              <tr>
                <th>Transaction</th>
                <th>Amount</th>
                <th>Effective Date</th>
                <th>Posting Date</th>
                <th>Transaction Status</th>
              </tr>
            </thead>
            <tbody>
              {searchResults.map((a) => (
                <React.Fragment key={a.transactionId}>
                  <tr>
                    <td>
                      {a.balanceMovementType} {a.description != null ? '(' + a.description + ')' : ''}
                      <div className="ui grey sub header">{a.transactionId}<CopyToClipboardIcon value={a.transactionId} /></div>
                    </td>
                    <td>{toCurrency(a.amount.value)}</td>
                    <td>{toFormattedDate(a.effectiveDate, 'yyyy-MM-dd HH:mm:ss')}</td>
                    <td>{toFormattedDate(a.postingDate, 'yyyy-MM-dd HH:mm:ss')}</td>
                    <td>
                      {a.transactionStatus}
                      <div className="top-right-hover">
                        <i
                          className="edit link icon small"
                          onClick={() => {
                            setShow(true);
                            setTransaction(a);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
              <AdjustmentModal
                show={show}
                onHide={() => setShow(false)}
                renderBackdrop={renderBackdrop}
                aria-labelledby="modal-label">
                <Adjustment
                  originalTransactionId={transaction?.transactionId}
                  clearModal={() => {
                    setShow(false);
                    transactionStateUpdatedCallback();
                  }}
                  originalAmount={transaction?.amount?.value}
                />
              </AdjustmentModal>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
